(() => {
  const SECTION_SELECTOR = ".shopify-section--main-product";
  const KACHING_SELECTOR = ".kaching-bundles";
  const SEAL_SELECTOR = ".sealsubs-container";
  const observedSources = new WeakSet();
  const settingsCache = new WeakMap();
  let frameId = null;

  function parseMoneyCents(text) {
    const amount = text?.match(/\d[\d.,\s\u00a0]*/)?.[0].replace(/\s|\u00a0/g, "");
    if (!amount) return null;

    const separator = Math.max(amount.lastIndexOf(","), amount.lastIndexOf("."));
    const hasCents = separator !== -1 && amount.length - separator - 1 === 2;
    const value = Number(amount.replace(/\D/g, ""));

    return Number.isFinite(value) ? value * (hasCents ? 1 : 100) : null;
  }

  function getNativePriceCents(productInfo) {
    const salePrice = productInfo.querySelector(
      ".product-info__price .price-list--lg sale-price:not(.corridge-selected-total-price)",
    );
    const priceText = Array.from(salePrice?.childNodes || [])
      .filter((node) => !(node instanceof Element && node.classList.contains("sr-only")))
      .map((node) => node.textContent)
      .join("");

    return parseMoneyCents(priceText);
  }

  function getNativeCompareAtPriceCents(productInfo) {
    const compareAtPrice = productInfo.querySelector(
      ".product-info__price .price-list--lg compare-at-price:not(.corridge-selected-total-compare-at-price)",
    );
    const priceText = Array.from(compareAtPrice?.childNodes || [])
      .filter((node) => !(node instanceof Element && node.classList.contains("sr-only")))
      .map((node) => node.textContent)
      .join("");

    return parseMoneyCents(priceText);
  }

  function getSettings(script) {
    if (!settingsCache.has(script)) {
      try {
        settingsCache.set(script, JSON.parse(script.textContent));
      } catch {
        settingsCache.set(script, null);
      }
    }

    return settingsCache.get(script);
  }

  function getActiveSettings(productInfo, block) {
    const productId = productInfo.closest("product-rerender")?.id.match(/^product-info-(\d+)-/)?.[1];
    if (!productId || !block) return null;

    const settings = Array.from(
      document.querySelectorAll(`script.kaching-bundles-deal-block-settings[data-product-id="${productId}"]`),
      getSettings,
    ).filter(Boolean);

    return settings.find((item) =>
      item.id === block.dataset.dealBlockId &&
      item.abTestVariantId === block.dataset.abTestVariantId,
    ) || (settings.length === 1 ? settings[0] : null);
  }

  function observeSource(source) {
    if (!source || observedSources.has(source)) return;

    observedSources.add(source);
    new MutationObserver(scheduleUpdate).observe(source, {
      attributes: true,
      attributeFilter: ["class", "checked", "aria-checked", "data-selling-plan-id"],
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  function getSelectedOffer(bar, settings, nativeUnitCents, subscriptionUnitCents) {
    const priceCents = parseMoneyCents(bar.querySelector(".kaching-bundles__bar-price")?.textContent);
    const deal = settings?.dealBars?.find((item) => item.id === bar.dataset.dealBarId);
    const quantity = getOfferQuantity(bar, deal);

    if (!Number.isSafeInteger(quantity) || quantity < 1) return null;

    if (subscriptionUnitCents !== null) {
      return { quantity, totalCents: subscriptionUnitCents * quantity };
    }

    if (deal?.discountType === "specific") {
      const configuredTotalCents = Math.round(Number(deal.discountValue) * 100);

      if (Number.isFinite(configuredTotalCents)) {
        return { quantity, totalCents: configuredTotalCents };
      }
    }

    if (priceCents === null) return null;

    const isPerItem = typeof settings?.showPricesPerItem === "boolean"
      ? settings.showPricesPerItem
      : priceCents < nativeUnitCents * 1.5;

    return { quantity, totalCents: priceCents * (isPerItem ? quantity : 1) };
  }

  function getOfferQuantity(bar, deal) {
    return Number(deal?.quantity || bar.querySelector(".kaching-bundles__bar-title")
      ?.textContent.match(/^\s*(\d+)\s*[x×]/i)?.[1]);
  }

  function updateKachingSubscriptionPrices(block, settings, subscriptionUnitCents) {
    const bars = block?.querySelectorAll(
      ".kaching-bundles__bars:not(.kaching-bundles__subscriptions) .kaching-bundles__bar",
    );
    if (!bars) return;

    bars.forEach((bar) => {
      const price = bar.querySelector(".kaching-bundles__bar-price");
      if (!price) return;

      if (subscriptionUnitCents === null) {
        delete price.dataset.corridgeSubscriptionTotalCents;
        return;
      }

      const deal = settings?.dealBars?.find((item) => item.id === bar.dataset.dealBarId);
      const quantity = getOfferQuantity(bar, deal);
      if (!Number.isSafeInteger(quantity) || quantity < 1) return;

      const totalCents = subscriptionUnitCents * quantity;
      const formattedAmount = new Intl.NumberFormat(document.documentElement.lang || "de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(totalCents / 100);
      const updatedText = price.textContent.replace(/\d[\d.,\s\u00a0]*/, formattedAmount);

      if (price.textContent.trim() !== updatedText) price.textContent = updatedText;
      price.dataset.corridgeSubscriptionTotalCents = String(totalCents);
    });
  }

  function formatMoneyCents(cents) {
    const locale = document.documentElement.lang || "de-DE";
    const currency = window.Shopify?.currency?.active || "EUR";

    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  function updateUnitPriceElement(unitPrice, nativeUnitCents, selectedOffer) {
    if (!unitPrice || nativeUnitCents <= 0 || selectedOffer.quantity < 1) return;

    if (!unitPrice.dataset.nativeText) {
      unitPrice.dataset.nativeText = unitPrice.textContent.trim();
      const nativePriceCents = parseMoneyCents(unitPrice.dataset.nativeText);
      if (nativePriceCents !== null) {
        unitPrice.dataset.nativePriceCents = String(nativePriceCents);
      }
    }

    const nativePriceCents = Number(unitPrice.dataset.nativePriceCents);
    if (!Number.isFinite(nativePriceCents)) return;

    const bundleUnitPriceCents = Math.round(
      nativePriceCents * selectedOffer.totalCents / (nativeUnitCents * selectedOffer.quantity),
    );
    const formattedAmount = new Intl.NumberFormat(document.documentElement.lang || "de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(bundleUnitPriceCents / 100);
    const updatedText = unitPrice.dataset.nativeText.replace(
      /\d[\d.,\s\u00a0]*/,
      formattedAmount,
    );

    if (unitPrice.textContent.trim() !== updatedText) unitPrice.textContent = updatedText;
    unitPrice.dataset.bundleUnitPriceCents = String(bundleUnitPriceCents);
  }

  function updateUnitPrice(productInfo, nativeUnitCents, selectedOffer) {
    updateUnitPriceElement(
      productInfo.querySelector(".product-info__price-details > unit-price"),
      nativeUnitCents,
      selectedOffer,
    );
  }

  function updateQuickAddPrices(section, nativeUnitCents, selectedOffer, compareAtTotalCents) {
    const priceList = section.querySelector(
      "product-quick-add .product-quick-add__variant .price-list",
    );
    const salePrice = priceList?.querySelector(":scope > sale-price");
    if (!priceList || !salePrice) return;

    const valueNode = Array.from(salePrice.childNodes)
      .find((node) => node.nodeType === Node.TEXT_NODE && parseMoneyCents(node.nodeValue) !== null);
    const formattedPrice = formatMoneyCents(selectedOffer.totalCents);
    if (valueNode && valueNode.nodeValue.trim() !== formattedPrice) {
      valueNode.nodeValue = formattedPrice;
    }
    salePrice.dataset.totalCents = String(selectedOffer.totalCents);

    const compareAtPrice = priceList.querySelector(":scope > compare-at-price");
    if (compareAtPrice) {
      const compareAtValueNode = Array.from(compareAtPrice.childNodes)
        .find((node) => node.nodeType === Node.TEXT_NODE && parseMoneyCents(node.nodeValue) !== null);
      const showCompareAtPrice = compareAtTotalCents !== null && compareAtTotalCents > selectedOffer.totalCents;

      compareAtPrice.hidden = !showCompareAtPrice;
      if (showCompareAtPrice && compareAtValueNode) {
        const formattedCompareAtPrice = formatMoneyCents(compareAtTotalCents);
        if (compareAtValueNode.nodeValue.trim() !== formattedCompareAtPrice) {
          compareAtValueNode.nodeValue = formattedCompareAtPrice;
        }
        compareAtPrice.dataset.totalCents = String(compareAtTotalCents);
      }
    }

    updateUnitPriceElement(
      priceList.querySelector(":scope > unit-price"),
      nativeUnitCents,
      selectedOffer,
    );
  }

  function getSavingsPercentage(regularTotalCents, finalTotalCents) {
    if (regularTotalCents <= 0 || finalTotalCents >= regularTotalCents) return 0;

    return Math.round(((regularTotalCents - finalTotalCents) / regularTotalCents) * 1000) / 10;
  }

  function formatPercentage(value) {
    return new Intl.NumberFormat(document.documentElement.lang || "de-DE", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(value);
  }

  function updateSavingsBadge(productInfo, savingsPercentage) {
    const priceRow = productInfo.querySelector(".product-info__price .rating-with-text");
    if (!priceRow) return;

    let badge = priceRow.querySelector(".corridge-selected-savings-badge");

    if (!badge) {
      badge = document.createElement("span");
      badge.className = "badge badge--on-sale corridge-selected-savings-badge";
      badge.setAttribute("aria-live", "polite");
      priceRow.append(badge);
    }

    badge.hidden = savingsPercentage <= 0;
    badge.dataset.savingsPercentage = String(savingsPercentage);

    if (badge.hidden) {
      badge.textContent = "";
    } else {
      const badgeText = `Spare ${formatPercentage(savingsPercentage)}%`;
      if (badge.textContent !== badgeText) badge.textContent = badgeText;
    }
  }

  function updateSubscriptionSavingsBadge(seal, regularUnitCents, subscriptionUnitCents) {
    const badge = seal?.querySelector(
      ".sls-option-container[data-selling-plan-group] .sls-savings-badge",
    );
    if (!badge || subscriptionUnitCents === null) return;

    const savingsPercentage = Math.round(
      getSavingsPercentage(regularUnitCents, subscriptionUnitCents),
    );
    const badgeText = `${formatPercentage(savingsPercentage)}% Rabatt`;

    if (badge.textContent !== badgeText) badge.textContent = badgeText;
    badge.dataset.savingsPercentage = String(savingsPercentage);
  }

  function updateHeadlinePrice(productInfo, totalCents, compareAtTotalCents) {
    const priceList = productInfo.querySelector(".product-info__price .price-list--lg");
    const nativeSalePrice = priceList?.querySelector(
      ":scope > sale-price:not(.corridge-selected-total-price)",
    );
    if (!priceList || !nativeSalePrice) return;

    let selectedPrice = priceList.querySelector(":scope > .corridge-selected-total-price");

    if (!selectedPrice) {
      selectedPrice = document.createElement("sale-price");
      selectedPrice.className = `${nativeSalePrice.className} corridge-selected-total-price`;
      selectedPrice.setAttribute("aria-live", "polite");

      const accessibleLabel = nativeSalePrice.querySelector(".sr-only")?.cloneNode(true);
      if (accessibleLabel) selectedPrice.append(accessibleLabel);
      selectedPrice.append(document.createTextNode(""));
      priceList.append(selectedPrice);
    }

    const valueNode = Array.from(selectedPrice.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
    const formattedPrice = formatMoneyCents(totalCents);
    if (valueNode && valueNode.nodeValue !== formattedPrice) valueNode.nodeValue = formattedPrice;

    selectedPrice.dataset.totalCents = String(totalCents);

    let selectedCompareAtPrice = priceList.querySelector(
      ":scope > .corridge-selected-total-compare-at-price",
    );

    if (compareAtTotalCents !== null && compareAtTotalCents > totalCents) {
      if (!selectedCompareAtPrice) {
        const nativeCompareAtPrice = priceList.querySelector(
          ":scope > compare-at-price:not(.corridge-selected-total-compare-at-price)",
        );
        selectedCompareAtPrice = document.createElement("compare-at-price");
        selectedCompareAtPrice.className = `${nativeCompareAtPrice?.className || "text-subdued line-through"} corridge-selected-total-compare-at-price`;
        selectedCompareAtPrice.setAttribute("aria-live", "polite");

        const accessibleLabel = nativeCompareAtPrice?.querySelector(".sr-only")?.cloneNode(true);
        if (accessibleLabel) selectedCompareAtPrice.append(accessibleLabel);
        selectedCompareAtPrice.append(document.createTextNode(""));
        priceList.append(selectedCompareAtPrice);
      }

      const compareAtValueNode = Array.from(selectedCompareAtPrice.childNodes)
        .find((node) => node.nodeType === Node.TEXT_NODE);
      const formattedCompareAtPrice = formatMoneyCents(compareAtTotalCents);
      if (compareAtValueNode && compareAtValueNode.nodeValue !== formattedCompareAtPrice) {
        compareAtValueNode.nodeValue = formattedCompareAtPrice;
      }
      selectedCompareAtPrice.hidden = false;
      selectedCompareAtPrice.dataset.totalCents = String(compareAtTotalCents);
    } else if (selectedCompareAtPrice) {
      selectedCompareAtPrice.hidden = true;
    }

    priceList.classList.add("corridge-selected-total-active");
  }

  function updatePortionPrices() {
    frameId = null;

    document.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      const productInfo = section.querySelector(".product-info");
      const portionLine = productInfo?.querySelector(".corridge-portions-per-day");
      const portions = Number(portionLine?.dataset.portionsPerUnit);
      if (!portionLine || !Number.isSafeInteger(portions) || portions < 1) return;

      const nativeUnitCents = getNativePriceCents(productInfo);
      if (nativeUnitCents === null) return;
      const nativeCompareAtUnitCents = getNativeCompareAtPriceCents(productInfo);

      const kaching = productInfo.querySelector(KACHING_SELECTOR);
      const seal = productInfo.querySelector(SEAL_SELECTOR);
      observeSource(kaching);
      observeSource(seal);

      const subscriptionOption = seal?.querySelector(
        ".sls-option-container[data-selling-plan-group]",
      );
      const offeredSubscriptionUnitCents = subscriptionOption
        ? parseMoneyCents(subscriptionOption.querySelector(".sls-price")?.textContent)
        : null;
      const subscriptionUnitCents = subscriptionOption?.classList.contains("sls-active")
        ? offeredSubscriptionUnitCents
        : null;
      updateSubscriptionSavingsBadge(
        seal,
        nativeCompareAtUnitCents ?? nativeUnitCents,
        offeredSubscriptionUnitCents,
      );
      const block = kaching?.querySelector(".kaching-bundles__block--loaded");
      const settings = getActiveSettings(productInfo, block);
      updateKachingSubscriptionPrices(block, settings, subscriptionUnitCents);
      const selectedBar = block?.querySelector(
        ".kaching-bundles__bars:not(.kaching-bundles__subscriptions) .kaching-bundles__bar--selected",
      );
      const selectedOffer = selectedBar
        ? getSelectedOffer(selectedBar, settings, nativeUnitCents, subscriptionUnitCents)
        : { quantity: 1, totalCents: subscriptionUnitCents ?? nativeUnitCents };
      if (!selectedOffer) return;

      const totalPortions = selectedOffer.quantity * portions;
      const dailyCents = Math.round(selectedOffer.totalCents / totalPortions);
      const regularUnitCents = nativeCompareAtUnitCents ?? nativeUnitCents;
      const regularTotalCents = regularUnitCents * selectedOffer.quantity;
      const savingsPercentage = Math.round(
        getSavingsPercentage(regularTotalCents, selectedOffer.totalCents),
      );
      const priceText = `${new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(dailyCents / 100)} € pro Tag`;
      const portionsElement = portionLine.querySelector(".corridge-portions-per-day__count");
      const priceElement = portionLine.querySelector(".corridge-portions-per-day__price");

      if (portionsElement && portionsElement.textContent !== String(totalPortions)) {
        portionsElement.textContent = String(totalPortions);
      }
      if (priceElement.textContent !== priceText) priceElement.textContent = priceText;
      const compareAtTotalCents = nativeCompareAtUnitCents === null
        ? null
        : nativeCompareAtUnitCents * selectedOffer.quantity;
      updateHeadlinePrice(productInfo, selectedOffer.totalCents, compareAtTotalCents);
      updateUnitPrice(productInfo, nativeUnitCents, selectedOffer);
      updateQuickAddPrices(section, nativeUnitCents, selectedOffer, compareAtTotalCents);
      updateSavingsBadge(productInfo, savingsPercentage);
      portionLine.classList.add("is-ready");
    });
  }

  function scheduleUpdate() {
    if (frameId !== null) return;
    frameId = window.requestAnimationFrame(updatePortionPrices);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleUpdate, { once: true });
  } else {
    scheduleUpdate();
  }

  new MutationObserver(scheduleUpdate).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  ["click", "change"].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      if (event.target.closest?.(`${KACHING_SELECTOR}, ${SEAL_SELECTOR}`)) scheduleUpdate();
    });
  });

  document.addEventListener("shopify:section:load", scheduleUpdate);
})();
